import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { TParsedCharacter } from '@lib/core/characters/utils/parseCharacter';
import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import { selectAnonymousUserProfileId } from '@lib/core/users/selectors/anonymousUser';
import { carryUserDataApiUrlCreator } from '@lib/core/users/slices/urls';
import { TUserDataField } from '@lib/core/users/types';

export interface IAnonymousUserSlice {
  character: TParsedCharacter;
  profileId: string;
  isLoading: boolean;
}

const initialState: IAnonymousUserSlice = {
  character: undefined,
  isLoading: false,
  profileId: '',
};

export const actionCarryAnonymousUserData = createTypedAsyncThunk(
  'actionCarryAnonymousUserData',
  async (fields: TUserDataField[], { getState }) => {
    try {
      const state = getState();
      const sourceProfile = selectAnonymousUserProfileId(state);

      await request({
        body: { fields, sourceProfile },
        method: REQUEST_METHODS.POST,
        url: carryUserDataApiUrlCreator(),
      });
      return null;
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const anonymousUserSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionCarryAnonymousUserData.pending, state => {
      state.isLoading = true;
    });
    builder.addCase(actionCarryAnonymousUserData.fulfilled, state => {
      state.isLoading = false;
    });
    builder.addCase(actionCarryAnonymousUserData.rejected, state => {
      state.isLoading = false;
    });
  },
  initialState,
  name: 'anonymousUser',
  reducers: {
    actionResetAnonymousUserData: () => initialState,
    actionSetAnonymousUserCharacter: (state, action: PayloadAction<TParsedCharacter>) => {
      state.character = action.payload;
    },
    actionSetAnonymousUserProfileId: (state, action: PayloadAction<string>) => {
      state.profileId = action.payload;
    },
  },
});

export const { actionSetAnonymousUserCharacter, actionSetAnonymousUserProfileId, actionResetAnonymousUserData } =
  anonymousUserSlice.actions;

export default anonymousUserSlice.reducer;
